import classnames from 'classnames';
import React, { useState } from 'react';
import { FormCheck, Table } from 'react-bootstrap';
import Truncate from 'react-truncate';
import { formatDataFileType, formatTextNewlines } from 'v2/utils/textFormat';
import { DataFileEntity } from '../models';
import styles from './dataFilestable.module.scss';
import { formatDate } from 'v2/utils/dateUtils';

type DataFileTableProps = {
  list: DataFileEntity[];
  actions?: (item: DataFileEntity) => JSX.Element;
  className?: string;
  showFilesCounter?: boolean;
  showCheckBox?: boolean;
};

type SelectSingleRowProps = {
  selectedRow?: DataFileEntity | null;
  setSelectedRow?: (item: DataFileEntity | null) => void;
  selectedRows?: never;
  setSelectedRows?: never;
};

type SelectMultipleRowsProps = {
  selectedRow?: never;
  setSelectedRow?: never;
  selectedRows?: DataFileEntity[] | null;
  setSelectedRows?: (item: DataFileEntity[] | null) => void;
};

interface TableItemProps {
  item: DataFileEntity;
  actions?: (item: DataFileEntity) => JSX.Element;
  className?: string;
  onClick?: () => void;
  showCheckBox?: boolean;
  selected?: boolean;
}

const TableItem = ({
  item,
  actions,
  className,
  onClick,
  showCheckBox,
  selected,
}: TableItemProps) => {
  const MAX_DESCRIPTION_LINES = 2;

  const [truncated, setTruncated] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleTruncate = (_truncated: boolean) => {
    if (_truncated && _truncated !== truncated) {
      setTruncated(_truncated);
    }
  };

  return (
    <tr
      key={item.id}
      className={classnames(className, styles.tableItem)}
      onClick={onClick}
    >
      {showCheckBox ? (
        <td className={styles.checkboxCol}>
          <FormCheck
            onChange={onClick}
            checked={!!selected}
            custom
            type="checkbox"
            aria-label="selectFileCheckBox"
            className={styles.checkBox}
          />
        </td>
      ) : null}
      <td className={styles.colName}>{item.name}</td>
      <td className={styles.colType}>{formatDataFileType(item.file_type)}</td>
      <td className={styles.dateCol}>{formatDate(item.created_at, false)}</td>
      <td className={styles.descriptionColumn}>
        <div className={styles.descriptionContainer}>
          <>
            <Truncate
              lines={!expanded && MAX_DESCRIPTION_LINES}
              onTruncate={handleTruncate}
            >
              {formatTextNewlines(item.description)}
            </Truncate>

            {truncated ? (
              <>
                {' '}
                <button
                  className={classnames(styles.expandCollapseDescriptionBtn)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(!expanded);
                  }}
                >
                  {expanded ? 'Read less' : 'Read more'}
                </button>
              </>
            ) : null}
          </>
        </div>
      </td>
      <td className={styles.colActions}>
        <div>{actions?.(item)}</div>
      </td>
    </tr>
  );
};

const DataFileTable = ({
  list,
  actions,
  className,
  showFilesCounter = true,
  selectedRow,
  setSelectedRow,
  selectedRows,
  setSelectedRows,
  showCheckBox,
}: DataFileTableProps & (SelectSingleRowProps | SelectMultipleRowsProps)) => {
  const isRowSelected = (item: DataFileEntity) => {
    if (selectedRow) {
      return item.id === selectedRow.id;
    } else if (selectedRows) {
      return !!selectedRows.find((selectedRow) => selectedRow.id === item.id);
    }
  };

  const toggleSelectedRow = (item: DataFileEntity) => {
    if (setSelectedRow) {
      setSelectedRow(item);
    } else if (setSelectedRows) {
      const isSelected = isRowSelected(item);
      if (isSelected) {
        setSelectedRows(
          selectedRows?.filter((selectedRow) => selectedRow.id !== item.id) ||
            null
        );
      } else {
        setSelectedRows([...(selectedRows || []), item]);
      }
    }
  };

  const toggleSelectAll = () => {
    if (selectedRows?.length === list.length) {
      setSelectedRows?.(null);
    } else {
      setSelectedRows?.(list);
    }
  };

  return (
    <>
      {showFilesCounter ? (
        <p className={styles.fileCounter}>
          {list.length} File
          {list.length > 1 || list.length === 0 ? 's' : ''}
        </p>
      ) : null}

      {!list.length ? (
        <div>
          <p className={classnames(styles.noFilesText, 'noFilesText')}>
            No files uploaded yet
          </p>
        </div>
      ) : (
        <Table hover borderless className={classnames(styles.table, className)}>
          <thead className={styles.tableHead}>
            <tr>
              {showCheckBox && setSelectedRows ? (
                <th onClick={toggleSelectAll}>
                  <FormCheck
                    onChange={toggleSelectAll}
                    checked={selectedRows?.length === list.length}
                    custom
                    type="checkbox"
                    aria-label="selectAllFilesCheckBox"
                  />
                </th>
              ) : null}
              <th>Name</th>
              <th>Type</th>
              <th>Upload Date</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {list.map((item) => (
              <TableItem
                key={item.id}
                className={classnames(
                  {
                    [styles.selected]: isRowSelected(item),
                  },
                  styles.tableItemContainerd
                )}
                item={item}
                actions={actions}
                onClick={() => toggleSelectedRow(item)}
                showCheckBox={showCheckBox}
                selected={isRowSelected(item)}
              />
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
};

export default DataFileTable;
